/** Minimal File System Access API types for saving into a user-picked folder. */
export interface SaveDirectoryHandle {
  kind: 'directory';
  name: string;
  getFileHandle(name: string, options?: { create?: boolean }): Promise<{
    createWritable(): Promise<{
      write(data: Blob): Promise<void>;
      close(): Promise<void>;
    }>;
  }>;
}

interface SaveDirectoryPickerWindow extends Window {
  showDirectoryPicker?(options?: { mode?: 'read' | 'readwrite' }): Promise<SaveDirectoryHandle>;
}

function pickerWindow(): SaveDirectoryPickerWindow | null {
  if (typeof window === 'undefined') return null;
  return window as unknown as SaveDirectoryPickerWindow;
}

/** Folder picker for saving files (HTTPS / localhost; Chrome, Edge, etc.). */
export function isSaveFolderAvailable(): boolean {
  const w = pickerWindow();
  return Boolean(w?.isSecureContext && typeof w.showDirectoryPicker === 'function');
}

export function sanitizeAttachmentFilename(filename: string): string {
  return filename.replace(/[\\/:*?"<>|]/g, '_');
}

async function resolveUniqueFilename(dir: SaveDirectoryHandle, filename: string): Promise<string> {
  const dot = filename.lastIndexOf('.');
  const base = dot > 0 ? filename.slice(0, dot) : filename;
  const ext = dot > 0 ? filename.slice(dot) : '';

  let candidate = filename;
  for (let n = 1; n < 1000; n++) {
    try {
      await dir.getFileHandle(candidate, { create: false });
      candidate = `${base} (${n})${ext}`;
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotFoundError') {
        return candidate;
      }
      throw err;
    }
  }
  return `${base}-${Date.now()}${ext}`;
}

/** Ask the user to pick a folder. Returns null if cancelled or unavailable. */
export async function pickSaveFolder(): Promise<SaveDirectoryHandle | null> {
  const w = pickerWindow();
  if (!w?.isSecureContext || typeof w.showDirectoryPicker !== 'function') {
    return null;
  }
  try {
    return await w.showDirectoryPicker({ mode: 'readwrite' });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      return null;
    }
    throw err;
  }
}

export async function writeBlobToDirectory(
  dir: SaveDirectoryHandle,
  filename: string,
  blob: Blob,
): Promise<string> {
  const safeName = sanitizeAttachmentFilename(filename);
  const uniqueName = await resolveUniqueFilename(dir, safeName);
  const fileHandle = await dir.getFileHandle(uniqueName, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(blob);
  await writable.close();
  return uniqueName;
}
